/**
 * Landing Pages — Lista y gestión de landing pages de marketing.
 * Ruta: /online-store/landing-pages
 */

import { useState, useEffect, useCallback , useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Modal,
  TextField,
  Select,
  Badge,
  EmptyState,
  Box,
  Spinner,
  IndexTable,
  Pagination,
  Tabs,
} from '@/lib/polaris';
import {
  PageIcon,
  PlusIcon,
  ViewIcon,
  DuplicateIcon,
  DeleteIcon,
  ExternalIcon,
} from '@/lib/polaris-icons';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { getErrorMessage } from '@/lib/getErrorMessage';

interface LandingPage {
  id: string;
  title: string;
  slug: string;
  template_type: string;
  status: string;
  total_views: number;
  total_submissions: number;
  conversion_rate: number;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_TONES: Record<string, 'success' | 'warning' | 'info' | 'new'> = {
  published: 'success',
  draft: 'info',
  archived: 'new',
};

const STATUS_LABELS: Record<string, string> = {
  published: 'Publicada',
  draft: 'Borrador',
  archived: 'Archivada',
};

const TEMPLATE_LABELS: Record<string, string> = {
  blank: 'En blanco',
  lead_capture: 'Captura de leads',
  webinar: 'Webinar',
  product_launch: 'Lanzamiento',
  promo: 'Promoción',
  coming_soon: 'Próximamente',
  thank_you: 'Gracias',
};

const TEMPLATE_OPTIONS = Object.entries(TEMPLATE_LABELS).map(([value, label]) => ({ label, value }));

const TAB_FILTERS = [
  { id: 'all', content: 'Todas' },
  { id: 'published', content: 'Publicadas' },
  { id: 'draft', content: 'Borradores' },
  { id: 'archived', content: 'Archivadas' },
];

function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'landing';
}

export default function LandingPagesListPage() {
  const storeNavigate = useStoreNavigate();
  const { tenantId } = useAuth();
  const toast = useAdminToast();

  const [page, setPage] = useState(1);
  const [selectedTab, setSelectedTab] = useState(0);
  const pageSize = 25;

  // Create dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newTemplate, setNewTemplate] = useState('lead_capture');
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<LandingPage | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Por la caché (ADR-0012, paso 3). La clave lleva pestaña y página: es lo que hace que volver a
  // la misma vista no pida nada.
  const consulta = useQuery({
    queryKey: ['landing-pages', tenantId, selectedTab, page],
    enabled: !!tenantId,
    // 🪤 Sin esto la tabla se vacía al cambiar de pestaña o de página, y se ve como un parpadeo.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: async (): Promise<{ pages: LandingPage[]; total: number }> => {
      let query = supabase
        .from('landing_pages')
        .select('id, title, slug, template_type, status, total_views, total_submissions, conversion_rate, published_at, created_at, updated_at', { count: 'exact' })
        .eq('tenant_id', tenantId!);

      const tabId = TAB_FILTERS[selectedTab].id;
      if (tabId !== 'all') query = query.eq('status', tabId);

      const offset = (page - 1) * pageSize;
      query = query.order('updated_at', { ascending: false }).range(offset, offset + pageSize - 1);

      const { data, count, error } = await query;
      if (error) throw error;
      return { pages: (data ?? []) as LandingPage[], total: count ?? 0 };
    },
  });

  const pages = useMemo(() => consulta.data?.pages ?? [], [consulta.data]);
  const total = consulta.data?.total ?? 0;
  // 🪤 `isPending`, no `isFetching`: con `keepPreviousData` ya hay datos previos al paginar.
  const loading = !!tenantId && consulta.isPending;

  // 🪤 v5 sacó `onError`: avisar en el render sería un efecto en medio del render.
  useEffect(() => {
    if (consulta.error) toast.error('Error al cargar', getErrorMessage(consulta.error));
  }, [consulta.error, toast]);

  const loadPages = useCallback(async () => { await consulta.refetch(); }, [consulta]);



  // Auto-generate slug from title
  useEffect(() => {
    if (newTitle) setNewSlug(slugify(newTitle));
  }, [newTitle]);

  const handleCreate = async () => {
    if (!tenantId || !newTitle.trim()) return;
    setSaving(true);

    try {
      const { data, error } = await supabase
        .from('landing_pages')
        .insert({
          tenant_id: tenantId,
          title: newTitle.trim(),
          slug: newSlug.trim() || slugify(newTitle),
          template_type: newTemplate,
          sections_json: getDefaultSections(newTemplate),
        })
        .select('id')
        .single();

      if (error) throw error;
      toast.success('Landing page creada');
      setDialogOpen(false);
      if (data) storeNavigate(`/online-store/landing-pages/${data.id}/editor`);
    } catch (error: unknown) {
      toast.error('Error al crear', getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (lp: LandingPage) => {
    if (!tenantId) return;

    try {
      // Cargar la página completa
      const { data: full } = await supabase
        .from('landing_pages')
        .select('*')
        .eq('id', lp.id)
        .single();

      if (!full) return;

      const { error } = await supabase.from('landing_pages').insert({
        tenant_id: tenantId,
        title: `${full.title} (copia)`,
        slug: `${full.slug}-copia-${Date.now().toString(36)}`,
        template_type: full.template_type,
        sections_json: full.sections_json,
        settings_json: full.settings_json,
        form_enabled: full.form_enabled,
        form_fields: full.form_fields,
        form_submit_label: full.form_submit_label,
        form_success_message: full.form_success_message,
        custom_css: full.custom_css,
        meta_title: full.meta_title,
        meta_description: full.meta_description,
        status: 'draft',
      });

      if (error) throw error;
      toast.success('Página duplicada');
      await loadPages();
    } catch (error: unknown) {
      toast.error('Error al duplicar', getErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);

    try {
      const { error } = await supabase.from('landing_pages').delete().eq('id', deleteTarget.id);
      if (error) throw error;
      toast.success('Página eliminada');
      setDeleteTarget(null);
      await loadPages();
    } catch (error: unknown) {
      toast.error('Error al eliminar', getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const handleTogglePublish = async (lp: LandingPage) => {
    const newStatus = lp.status === 'published' ? 'draft' : 'published';
    const updates: Record<string, unknown> = { status: newStatus };
    if (newStatus === 'published') updates.published_at = new Date().toISOString();

    try {
      const { error } = await supabase.from('landing_pages').update(updates).eq('id', lp.id);
      if (error) throw error;
      toast.success(newStatus === 'published' ? 'Publicada' : 'Despublicada');
      await loadPages();
    } catch (error: unknown) {
      toast.error('Error', getErrorMessage(error));
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <Page
      title="Landing Pages"
      subtitle="Páginas de marketing con formularios y tracking"
      primaryAction={{
        content: 'Crear landing page',
        icon: PlusIcon,
        onAction: () => {
          setNewTitle('');
          setNewSlug('');
          setNewTemplate('lead_capture');
          setDialogOpen(true);
        },
      }}
    >
      <BlockStack gap="400">
        <Tabs tabs={TAB_FILTERS} selected={selectedTab} onSelect={(i) => { setSelectedTab(i); setPage(1); }} />

        {loading ? (
          <Card><Box padding="800"><InlineStack align="center"><Spinner size="large" /></InlineStack></Box></Card>
        ) : pages.length === 0 ? (
          <Card>
            <EmptyState
              heading="Sin landing pages"
              image=""
              action={{ content: 'Crear landing page', onAction: () => setDialogOpen(true) }}
            >
              <Text as="p">Crea páginas de aterrizaje para capturar leads y promocionar productos.</Text>
            </EmptyState>
          </Card>
        ) : (
          <Card padding="0">
            <IndexTable
              headings={[
                { title: 'Título' },
                { title: 'Estado' },
                { title: 'Template' },
                { title: 'Visitas' },
                { title: 'Envíos' },
                { title: 'Conversión' },
                { title: 'Actualizada' },
                { title: 'Acciones' },
              ]}
              itemCount={pages.length}
              selectable={false}
            >
              {pages.map((lp, idx) => (
                <IndexTable.Row
                  id={lp.id}
                  key={lp.id}
                  position={idx}
                  onClick={() => storeNavigate(`/online-store/landing-pages/${lp.id}/editor`)}
                >
                  <IndexTable.Cell>
                    <Text as="span" fontWeight="medium">{lp.title}</Text>
                    <br />
                    <Text as="span" variant="bodySm" tone="subdued">/l/{lp.slug}</Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Badge tone={STATUS_TONES[lp.status] ?? 'info'}>
                      {STATUS_LABELS[lp.status] ?? lp.status}
                    </Badge>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text as="span" variant="bodySm">{TEMPLATE_LABELS[lp.template_type] ?? lp.template_type}</Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text as="span" variant="bodySm">{lp.total_views.toLocaleString()}</Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text as="span" variant="bodySm">{lp.total_submissions.toLocaleString()}</Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text as="span" variant="bodySm" fontWeight={lp.conversion_rate > 5 ? 'bold' : 'regular'}>
                      {Number(lp.conversion_rate).toFixed(1)}%
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text as="span" variant="bodySm">{formatDate(lp.updated_at)}</Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <InlineStack gap="100">
                      <Button
                        icon={lp.status === 'published' ? ViewIcon : ExternalIcon}
                        variant="plain"
                        size="slim"
                        onClick={(e) => { e.stopPropagation(); handleTogglePublish(lp); }}
                        accessibilityLabel={lp.status === 'published' ? 'Despublicar' : 'Publicar'}
                      />
                      <Button
                        icon={DuplicateIcon}
                        variant="plain"
                        size="slim"
                        onClick={(e) => { e.stopPropagation(); handleDuplicate(lp); }}
                        accessibilityLabel="Duplicar"
                      />
                      <Button
                        icon={DeleteIcon}
                        variant="plain"
                        size="slim"
                        tone="critical"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(lp); }}
                        accessibilityLabel="Eliminar"
                      />
                    </InlineStack>
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </Card>
        )}

        {total > pageSize && (
          <InlineStack align="center">
            <Pagination
              hasPrevious={page > 1}
              onPrevious={() => setPage(page - 1)}
              hasNext={page * pageSize < total}
              onNext={() => setPage(page + 1)}
            />
          </InlineStack>
        )}
      </BlockStack>

      {/* Modal Crear */}
      <Modal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Nueva landing page"
        primaryAction={{ content: 'Crear', onAction: handleCreate, loading: saving, disabled: !newTitle.trim() }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setDialogOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Título"
              value={newTitle}
              onChange={setNewTitle}
              placeholder="Ej: Promo Black Friday 2026"
              autoComplete="off"
              requiredIndicator
            />
            <TextField
              label="URL slug"
              value={newSlug}
              onChange={setNewSlug}
              prefix="/l/"
              placeholder="promo-black-friday"
              autoComplete="off"
              helpText="Se genera automáticamente del título"
            />
            <Select
              label="Template"
              options={TEMPLATE_OPTIONS}
              value={newTemplate}
              onChange={setNewTemplate}
              helpText="Puedes cambiar el diseño después en el editor"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Modal Eliminar */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar landing page"
        primaryAction={{ content: 'Eliminar', onAction: handleDelete, loading: deleting, destructive: true }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setDeleteTarget(null) }]}
      >
        <Modal.Section>
          <Text as="p">
            ¿Eliminar "{deleteTarget?.title}"? Se perderán todos los envíos de formulario y analytics asociados.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}

// ─── Default sections por template ─────────────────────────
function getDefaultSections(template: string): object[] {
  switch (template) {
    case 'lead_capture':
      return [
        { section_type: 'hero', display_order: 0, settings: { heading: 'Título principal', subheading: 'Subtítulo persuasivo para capturar leads', button_label: '', show_overlay: true } },
        { section_type: 'rich-text', display_order: 1, settings: { heading: 'Beneficios', content: '<ul><li>Beneficio 1</li><li>Beneficio 2</li><li>Beneficio 3</li></ul>' } },
        { section_type: 'contact-form', display_order: 2, settings: { heading: 'Regístrate ahora', show_phone: true } },
      ];
    case 'webinar':
      return [
        { section_type: 'hero', display_order: 0, settings: { heading: 'Webinar Gratuito', subheading: 'Fecha y hora del evento', button_label: 'Registrarme' } },
        { section_type: 'multicolumn', display_order: 1, settings: { heading: 'Lo que aprenderás', columns: 3 } },
        { section_type: 'testimonials', display_order: 2, settings: { heading: 'Lo que dicen nuestros asistentes' } },
        { section_type: 'contact-form', display_order: 3, settings: { heading: 'Reserva tu lugar' } },
      ];
    case 'product_launch':
      return [
        { section_type: 'hero', display_order: 0, settings: { heading: 'Nuevo Producto', subheading: 'Descripción corta del producto' } },
        { section_type: 'image-with-text', display_order: 1, settings: { heading: 'Características', layout: 'image_first' } },
        { section_type: 'featured-product', display_order: 2, settings: {} },
        { section_type: 'contact-form', display_order: 3, settings: { heading: '¿Interesado? Déjanos tus datos' } },
      ];
    case 'promo':
      return [
        { section_type: 'hero', display_order: 0, settings: { heading: 'Oferta Especial', subheading: 'Solo por tiempo limitado' } },
        { section_type: 'countdown', display_order: 1, settings: { heading: 'La oferta termina en' } },
        { section_type: 'featured-collection', display_order: 2, settings: { heading: 'Productos en promoción' } },
      ];
    case 'coming_soon':
      return [
        { section_type: 'hero', display_order: 0, settings: { heading: 'Próximamente', subheading: 'Algo increíble viene en camino' } },
        { section_type: 'countdown', display_order: 1, settings: { heading: 'Faltan' } },
        { section_type: 'newsletter', display_order: 2, settings: { heading: 'Sé el primero en enterarte' } },
      ];
    case 'thank_you':
      return [
        { section_type: 'rich-text', display_order: 0, settings: { heading: '¡Gracias!', content: '<p>Hemos recibido tu información. Te contactaremos pronto.</p>' } },
      ];
    default: // blank
      return [
        { section_type: 'hero', display_order: 0, settings: { heading: 'Tu título aquí', subheading: 'Tu subtítulo aquí' } },
      ];
  }
}
